<template>
  <v-dialog
    v-model="dialog"
    :close-on-content-click="false"
    :close-on-click="false"
    origin="right top"
    left
    min-width="1000"
    scrollable
  >
    <template #activator="{ attrs, on }">
      <v-btn
        v-bind="attrs"
        v-on="on"
        :disabled="!spec"
        text
        small
      >
        Tilføj varer
        <v-icon right> mdi-plus </v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-toolbar flat>
        <v-toolbar-title>
          Tilføj varer
        </v-toolbar-title>

        <v-spacer />

        <div class="d-flex align-center" style="gap: 1rem;">
          <v-btn
            icon
            @click="close"
            >
            <v-icon>
              mdi-close
            </v-icon>
          </v-btn>
        </div>
      </v-toolbar>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <v-tabs v-model="activeTab" grow>
              <v-tab>Søg</v-tab>
              <v-tab>Kopier</v-tab>
            </v-tabs>

            <v-tabs-items v-model="activeTab">
              <v-tab-item class="px-1">
                <div
                  class="d-flex align-center"
                  style="gap: 1rem"
                >
                  <v-text-field
                    v-model="searchInput"
                    solo
                    autofocus
                    label="Søg efter vare"
                    hide-details
                    clearable
                    @submit.prevent
                    @keydown.enter.prevent="onSearchInputChange"
                  >
                    <template #append>
                      <v-btn
                        icon
                        @click="search"
                      >
                        <v-icon>
                          mdi-magnify
                        </v-icon>
                      </v-btn>
                    </template>
                  </v-text-field>
                  <v-checkbox
                    v-model="onlyFavorites"
                    label="Kun TPA favoritter"
                    @click="search"
                  />
                  <v-checkbox
                    v-model="onlyPreferred"
                    label="Kun foretrukket af kunde"
                    @click="search"
                  />
                </div>
                <v-data-table
                  :headers="headers"
                  :items="parts"
                  :server-items-length="partsCount"
                  :loading="loading"
                  :options.sync="pagination"
                  @click:row="onSelectRow"
                  @update:options="search"
                  dense
                >
                  <template #item="{ item }">
                    <tr :class="{ 'success white--text': isSelected(item) }" @click="onSelectRow(item)">
                      <td>{{ item.title }}</td>
                      <td>
                        <v-menu open-on-hover>
                          <template #activator="{ on, attrs }">
                            <v-img
                              v-on="on"
                              v-bind="attrs"
                              :src="item.imageUrl"
                              :lazy-src="require('@/assets/missing-image.png')"
                              height="40"
                              width="50"
                              contain
                            ></v-img>
                          </template>

                          <v-card>
                            <v-img
                              :src="item.imageUrl"
                              :lazy-src="require('@/assets/missing-image.png')"
                              height="400"
                              contain
                            ></v-img>
                          </v-card>
                        </v-menu>
                      </td>
                      <td>{{ item.ean }}</td>
                      <td>{{ item.manufacturerTypeReference }}</td>
                      <td class="text-right">{{ item.costPrice | formatNumberFixed2 }}</td>
                      <td class="text-right">{{ item.listPrice | formatNumberFixed2 }}</td>
                      <td class="text-right font-weight-bold">
                        {{ getCustomerPrice(item) | formatNumberFixed2 }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>

                <v-fade-transition>
                  <v-card v-if="!hasSearched" flat disabled>
                    <v-card-text class="d-flex justify-center py-8">
                      Søg efter varer i feltet ovenfor
                    </v-card-text>
                  </v-card>
                  <v-card
                    v-else-if="!parts.length"
                    flat
                    disabled
                  >
                    <v-card-text class="d-flex justify-center py-8">
                      Ingen resultater
                    </v-card-text>
                  </v-card>
                </v-fade-transition>
              </v-tab-item>

              <v-tab-item class="mt-1">
                <PartBulkCSVImport @import="onImport" />
              </v-tab-item>
            </v-tabs-items>
          </v-col>
          <v-col>
            <h4 class="mb-2">Valgte varer</h4>
            <v-slide-x-transition group>
              <v-card v-for="(line, index) of linesToImport" :key="index" class="mb-3">
                <v-card-text>
                  <b>{{ line.part.title }}</b>
                  <div>
                    <small>
                      {{ line.part.manufacturerTitle }}
                    </small>
                  </div>
                </v-card-text>
                <v-card-actions>
                  <div class="ml-auto d-flex align-center" style="gap: 1rem;">
                    <v-btn icon @click="removeLine(index)" tabindex="-1">
                      <v-icon>mdi-delete-outline</v-icon>
                    </v-btn>
                    <div style="width: 150px;">
                      <v-text-field
                        :ref="line.partNumber + '-quantity'"
                        v-model.number="line.quantity"
                        type="number"
                        label="Antal"
                        outlined
                        dense
                        hide-details
                        autofocus
                      />
                    </div>
                  </div>
                </v-card-actions>
              </v-card>
            </v-slide-x-transition>

            <v-fade-transition>
              <v-card v-if="!linesToImport.length" outlined>
                <v-card-text>Vælg nogle varer til venstre.</v-card-text>
              </v-card>
            </v-fade-transition>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-btn text @click="close">
          {{ $t('common.cancel') }}
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn
          color="success"
          :disabled="!linesToImport.length"
          :loading="creating"
          @click="add"
        >
          Tilføj
          <v-icon right>mdi-plus</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { Line } from '@/helpers/classes';
import { mapState } from 'vuex'
import PartBulkCSVImport from '../part/PartBulkCSVImport.vue';

export default {
  name: 'SpecificationSectionPartAdd',
  components: { PartBulkCSVImport },
  props: [
    'id',
  ],
  data() {
    return {
      dialog: false,
      activeTab: 'search',
      creating: false,
      loading: false,
      hasSearched: false,
      onlyFavorites: false,
      onlyPreferred: false,
      linesToImport: [],
      pagination: {
        page: 1,
        itemsPerPage: 15
      },
      page: 1,
      parts: [],
      partsCount: null,
      pageCount: null,
      searchInput: '',
      quantity: 1,
      headers: [
        {
          text: 'Navn',
          value: 'title',
          width: 500,
          sortable: false
        },
        {
          text: 'Billede',
          value: 'imageUrl',
          width: 100,
          sortable: false
        },
        {
          text: 'EAN',
          value: 'ean',
          sortable: false
        },
        {
          text: 'Typebetegnelse',
          value: 'manufacturerTypeReference',
          sortable: false
        },
        {
          text: 'Kostpris',
          value: 'costPrice',
          align: 'right',
          sortable: false
        },
        {
          text: 'Listepris',
          value: 'listPrice',
          align: 'right',
          sortable: false
        },
        {
          text: 'Kundepris',
          value: 'customerPrice',
          align: 'right',
          sortable: false
        }
      ]
    }
  },
  computed: {
    ...mapState({
      spec: state => state.specification.currentSpecification,
      primaryCustomerId: state => state.primaryCustomerId
    })
  },
  methods: {
    close() {
      this.dialog = false;
      this.searchInput = '';
      this.hasSearched = false;
      this.parts = [];
      this.partsCount = null;
      this.pagination.page = 1;
    },
    async add() {
      this.creating = true;

      const requests = this.linesToImport.map(line => {
        const { id, title, description, costPrice } = line.part;
  
        const data = {
          specificationSection_Id: this.id,
          part_Id: id ?? null,
          title,
          description,
          quantity: line.quantity ?? 1,
          costPrice,
          price: this.getPartCustomerDetail(line.part).price
        };

        return this.$api.post('specification/section/part', data)
        .then(({ data }) => {
          if (!data.success) throw 'Error creating section part';

          this.$emit('create:success', data.model);
          this.$notifier.showSuccess({ message: 'Materiale tilføjet' });
          this.close();
        })
      });
      
      try {
        await Promise.all(requests);
        this.linesToImport = [];
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.creating = false;
      }
    },
    async search() {
      const text = this.searchInput;

      try {
        this.loading = true;
        
        const { data } = await this.$api.post('adminpart/listsearch', {
          customerId: this.spec.relationCustomerId,
          favoritesOnly: this.onlyFavorites,
          isPreferredByCustomerOnly: this.onlyPreferred,
          page: this.pagination.page,
          pageSize: this.pagination.itemsPerPage,
          textSearch: text
        });

        if (data.data?.parts) {
          this.parts = data.data.parts;
        }

        if (data.data?.partsCount && data.data.partsCount !== this.partsCount) {
          this.partsCount = data.data?.partsCount;
        }

        if (data.data?.pageCount && data.data.pageCount !== this.pageCount) {
          this.pageCount = data.data?.pageCount;
        }
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
        this.hasSearched = true;
      }
    },

    getPartCustomerDetail (part) {
      return part.customers.find(customerDetail => customerDetail.title === this.spec.relationCustomerTitle);
    },
    onSearchInputChange() {
      this.parts = [];
      this.pagination.page = 1;
      this.partsCount = null;
      this.pageCount = null;
      this.search();
    },
    onSelectRow(item) {
      // If row is already added, stop here
      if (this.linesToImport.some(line => line.partNumber === item.partNumber)) return;

      const line = new Line(item.partNumber, null);
      line.part = item;

      this.linesToImport.push(line);
    },
    removeLine(index) {
      this.linesToImport.splice(index, 1);
    },
    isSelected(line) {
      return this.linesToImport.some(x => x.partNumber === line.partNumber)
    },
    onImport(lines) {
      lines.forEach(line => {
        const index = this.linesToImport.findIndex(x => x.partNumber === line.partNumber);

        if (index !== -1) {
          this.linesToImport[index].quantity += line.quantity;
        } else {
          this.linesToImport.push(line)
        }
      })
    },
    getCustomerPrice(part) {
      if (!part?.customers?.length) return null;

      const customer = part.customers.find(customerDetail => customerDetail.customerId === this.primaryCustomerId);

      return customer?.price;
    }
  },
  watch: {
    dialog(value) {
      if (value) {
        this.search();
      }
    }
  }
}
</script>

<style>
.v-data-table tr {
  cursor: pointer;
}
</style>